"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Icon, { type IconName } from "./Icon";
import { useAuth } from "./AuthProvider";
import { useSidebar } from "./SidebarProvider";
import { useTheme } from "./ThemeProvider";

interface NavItem {
  href: string;
  label: string;
  icon: IconName;
}

export const navItems: NavItem[] = [
  { href: "/", label: "Overview", icon: "grid" },
  { href: "/bells", label: "Live Bells", icon: "bell" },
  { href: "/deals", label: "Deals", icon: "handshake" },
  { href: "/customers", label: "Customers", icon: "lot" },
  { href: "/managers", label: "Team", icon: "users" },
  { href: "/users", label: "User Admin", icon: "user-plus" },
  { href: "/analytics", label: "Analytics", icon: "chart" },
  { href: "/audit", label: "Reports", icon: "download" },
  { href: "/notifications", label: "Notifications", icon: "bell" },
  { href: "/broadcasts", label: "Broadcasts", icon: "mail" },
  { href: "/support", label: "Support", icon: "message" },
  { href: "/content", label: "Content", icon: "edit" },
  { href: "/settings", label: "Configuration", icon: "settings" },
];

export default function NavList({
  onNavigate,
  collapsible = false,
}: {
  onNavigate?: () => void;
  /** Desktop rail only: turns the logo into a collapse toggle. */
  collapsible?: boolean;
}) {
  const pathname = usePathname();
  const { logout } = useAuth();
  const { collapsed, toggle } = useSidebar();
  const { resolved } = useTheme();

  // The mobile drawer renders the same list but is never collapsed.
  const mini = collapsible && collapsed;

  // The white mark is used for the collapsed rail, but only on the dark theme —
  // it is white-on-transparent and would disappear against the light surface.
  const logoSrc = mini && resolved === "dark" ? "/logo-white.png" : "/logo.png";

  return (
    <>
      {collapsible ? (
        <button
          onClick={toggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          className={`flex flex-col gap-1 px-2 pt-2 pb-4 cursor-pointer ${
            mini ? "items-center" : "items-start"
          }`}
        >
          <Image
            key={logoSrc}
            src={logoSrc}
            alt="The Bell System"
            width={68}
            height={40}
            priority
            // Height-constrained with w-auto, so the two marks' differing
            // intrinsic ratios both render correctly.
            className={mini ? "h-8 w-auto" : "h-10 w-auto"}
          />
          {!mini && <p className="text-[11px] text-text-faint tracking-wide">COMMAND CENTER</p>}
        </button>
      ) : (
        <Link href="/" onClick={onNavigate} className="flex flex-col items-start gap-1 px-2 pt-2 pb-4">
          <Image
            src="/logo.png"
            alt="The Bell System"
            width={68}
            height={40}
            priority
            className="h-10 w-auto"
          />
          <p className="text-[11px] text-text-faint tracking-wide">COMMAND CENTER</p>
        </Link>
      )}

      <nav className="flex flex-col gap-1.5 flex-1">
        {navItems.map((item) => {
          const on =
            item.href === "/"
              ? pathname === "/"
              : pathname.startsWith(item.href);
          return (
            <Link
              key={item.href}
              href={item.href}
              onClick={onNavigate}
              // Collapsed items lose their visible label, so the name has to
              // survive as the accessible name and the hover tooltip.
              title={mini ? item.label : undefined}
              aria-label={mini ? item.label : undefined}
              style={
                on
                  ? ({ "--tint-color": "var(--mint)" } as React.CSSProperties)
                  : undefined
              }
              className={`flex items-center rounded-2xl py-3 text-sm font-medium transition-all ${
                mini ? "justify-center px-0" : "gap-3 px-3.5"
              } ${on ? "neu-inset tint text-accent" : "nav-link text-text-muted"}`}
            >
              <Icon name={item.icon} size={19} className="shrink-0" />
              {!mini && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      <button
        onClick={() => {
          onNavigate?.();
          void logout();
        }}
        title={mini ? "Sign out" : undefined}
        aria-label={mini ? "Sign out" : undefined}
        className={`neu-btn flex items-center rounded-2xl py-3 text-sm text-text-muted hover:text-text transition-colors duration-300 ${
          mini ? "justify-center px-0" : "gap-3 px-3.5 text-left"
        }`}
      >
        <Icon name="logout" size={18} className="shrink-0" />
        {!mini && "Sign out"}
      </button>
    </>
  );
}